import {
  BriefcaseBusiness,
  CalendarCheck,
  MessageCircle,
  Star,
  UtensilsCrossed,
} from 'lucide-react';
import {
  FOODSTATION_PRODUCTS,
  DEFAULT_THEME,
  DEFAULT_PRODUCT_STATE,
} from '../../constants';
import { buildCustomColorsFromTheme } from '../../theme';

export const FALLBACK_RESTAURANT = {
  slug: '',
  name: 'Restaurante',
  tagline: 'Reservas, cardápio, atendimento e muito mais em um só lugar.',
  monogram: 'FS',
  address: '',
  hours: '',
  openingHours: null,
  verified: true,
  coverImage: null,
  profileImage: null,
  showAddress: true,
  showHours: true,
  showDirections: true,
  openStatus: null,
  promotion: {
    enabled: false,
    image: null,
  },
};

export const hasRegisteredOpeningHours = schedule =>
  [...(schedule?.days || []), ...(schedule?.exceptions || [])]
    .some(day => !day.closed && day.periods?.some(period => period.start && period.end && period.start !== period.end));

const baseProductId = (id) => (String(id).startsWith('onemenu_') ? 'onemenu' : id);

export const productMeta = (id) =>
  FOODSTATION_PRODUCTS.find((product) => product.id === baseProductId(id));

export const PRODUCT_ICONS = {
  reservo: CalendarCheck,
  reservo_waitlist: CalendarCheck,
  onemenu: UtensilsCrossed,
  onemenu_delivery: UtensilsCrossed,
  onemenu_in_person: UtensilsCrossed,
  feedbackhero: Star,
  curriculocerto: BriefcaseBusiness,
  atendabot: MessageCircle,
};

export const PRODUCT_LOGO_BACKGROUNDS = {
  reservo: '#ffdb2f',
  reservo_waitlist: '#ffdb2f',
  onemenu: '#fff6f3',
  onemenu_delivery: '#fff6f3',
  onemenu_in_person: '#fff6f3',
  feedbackhero: '#143023',
  curriculocerto: '#000',
  atendabot: '#3127A0',
};

export const normalizeUrl = (value) => {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

export const apiErrorMessage = (error, fallback) =>
  error?.response?.data?.error
  || error?.response?.data?.message
  || fallback;

export const LINKPAGE_IMAGE_MAX_SIZE = 5 * 1024 * 1024;
export const LINKPAGE_IMAGE_ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
export const PROFILE_CROP_FRAME_SIZE = 240;
export const PROFILE_CROP_OUTPUT_SIZE = 512;
export const COVER_CROP_FRAME_WIDTH = 240;
export const COVER_CROP_FRAME_HEIGHT = 100;
export const COVER_CROP_OUTPUT_WIDTH = 1200;
export const COVER_CROP_OUTPUT_HEIGHT = 500;
export const PROMOTION_CROP_FRAME_WIDTH = 240;
export const PROMOTION_CROP_FRAME_HEIGHT = 320;
export const PROMOTION_CROP_OUTPUT_WIDTH = 1200;
export const PROMOTION_CROP_OUTPUT_HEIGHT = 1600;
export const PROFILE_CROP_ZOOM_MIN = 0.25;
export const PROFILE_CROP_ZOOM_MAX = 4;
export const CROP_IMAGE_QUALITY = 0.82;
export const EMPTY_PROFILE_CROP = {
  file: null,
  previewUrl: '',
  target: 'profile',
  linkId: null,
  zoom: 1,
  x: 0,
  y: 0,
  naturalWidth: 0,
  naturalHeight: 0,
};

export const CUSTOM_COLOR_GROUPS = [
  {
    title: 'Página',
    fields: [
      { key: 'pageBg', label: 'Fundo externo' },
      { key: 'cardBg', label: 'Fundo da página' },
      { key: 'coverStart', label: 'Capa início' },
      { key: 'coverEnd', label: 'Capa fim' },
    ],
  },
  {
    title: 'Textos',
    fields: [
      { key: 'text', label: 'Texto principal' },
      { key: 'muted', label: 'Texto secundário' },
      { key: 'divider', label: 'Divisórias' },
      { key: 'infoChip', label: 'Chips de informação' },
    ],
  },
  {
    title: 'Marca',
    fields: [
      { key: 'accent', label: 'Destaque' },
      { key: 'accentInk', label: 'Texto no destaque' },
      { key: 'logoRing', label: 'Borda do logo' },
      { key: 'arrow', label: 'Seta dos botões' },
    ],
  },
  {
    title: 'Botões',
    fields: [
      { key: 'buttonBg', label: 'Fundo do botão' },
      { key: 'buttonBorder', label: 'Borda do botão' },
      { key: 'buttonText', label: 'Texto do botão' },
      { key: 'buttonSub', label: 'Subtexto do botão' },
      { key: 'iconBg', label: 'Fundo dos ícones' },
      { key: 'iconBorder', label: 'Borda dos ícones' },
    ],
  },
];

export const friendlyUploadErrorMessage = (error, fallback) => {
  const message = apiErrorMessage(error, fallback);
  const normalized = String(message || '').toLowerCase();

  if (normalized.includes('excede') || normalized.includes('5mb')) {
    return 'Imagem muito grande. Envie uma imagem de até 5MB.';
  }

  if (
    normalized.includes('arquivo inválido')
    || normalized.includes('arquivo invalido')
    || normalized.includes('jpg')
    || normalized.includes('png')
    || normalized.includes('webp')
  ) {
    return 'Formato de imagem inválido. Envie uma imagem JPG, PNG ou WebP.';
  }

  return message;
};

export const validateImageBeforeUpload = (file) => {
  if (!file) return '';

  if (!LINKPAGE_IMAGE_ALLOWED_TYPES.has(file.type)) {
    return 'Formato de imagem inválido. Envie uma imagem JPG, PNG ou WebP.';
  }

  if (file.size > LINKPAGE_IMAGE_MAX_SIZE) {
    return 'Imagem muito grande. Envie uma imagem de até 5MB.';
  }

  return '';
};

export const validateImageTypeBeforeUpload = (file) => {
  if (!file) return '';

  if (!LINKPAGE_IMAGE_ALLOWED_TYPES.has(file.type)) {
    return 'Formato de imagem inválido. Envie uma imagem JPG, PNG ou WebP.';
  }

  return '';
};

export const cropFrameDimensions = (target = 'profile', crop = {}) => {
  if (target === 'promotion') {
    const scale = crop.naturalWidth && crop.naturalHeight
      ? Math.min(
        PROMOTION_CROP_FRAME_WIDTH / crop.naturalWidth,
        PROMOTION_CROP_FRAME_HEIGHT / crop.naturalHeight
      )
      : 0;
    return scale
      ? { width: crop.naturalWidth * scale, height: crop.naturalHeight * scale }
      : { width: PROMOTION_CROP_FRAME_WIDTH, height: PROMOTION_CROP_FRAME_HEIGHT };
  }
  return target === 'cover'
    ? { width: COVER_CROP_FRAME_WIDTH, height: COVER_CROP_FRAME_HEIGHT }
    : { width: PROFILE_CROP_FRAME_SIZE, height: PROFILE_CROP_FRAME_SIZE };
};

export const cropOutputDimensions = (target = 'profile') =>
  target === 'promotion'
    ? { width: PROMOTION_CROP_OUTPUT_WIDTH, height: PROMOTION_CROP_OUTPUT_HEIGHT }
    : target === 'cover'
    ? { width: COVER_CROP_OUTPUT_WIDTH, height: COVER_CROP_OUTPUT_HEIGHT }
    : { width: PROFILE_CROP_OUTPUT_SIZE, height: PROFILE_CROP_OUTPUT_SIZE };

export const cropDisplaySize = (crop) => {
  if (!crop.naturalWidth || !crop.naturalHeight) {
    return { width: 0, height: 0 };
  }

  const frame = cropFrameDimensions(crop.target, crop);
  const baseScale = Math.max(
    frame.width / crop.naturalWidth,
    frame.height / crop.naturalHeight
  );

  return {
    width: crop.naturalWidth * baseScale * crop.zoom,
    height: crop.naturalHeight * baseScale * crop.zoom,
  };
};

export const clampCropPosition = (crop, x, y) => {
  const display = cropDisplaySize(crop);
  if (!display.width || !display.height) {
    return { x: 0, y: 0 };
  }

  const frame = cropFrameDimensions(crop.target, crop);
  const maxX = Math.abs(display.width - frame.width) / 2;
  const maxY = Math.abs(display.height - frame.height) / 2;

  return {
    x: maxX ? Math.min(maxX, Math.max(-maxX, x)) : 0,
    y: maxY ? Math.min(maxY, Math.max(-maxY, y)) : 0,
  };
};

export const cropFitZoom = (crop) => {
  if (!crop.naturalWidth || !crop.naturalHeight) return 1;
  const frame = cropFrameDimensions(crop.target, crop);
  return Math.min(frame.width / crop.naturalWidth, frame.height / crop.naturalHeight)
    / Math.max(frame.width / crop.naturalWidth, frame.height / crop.naturalHeight);
};

export const cropZoomMin = (crop) => Math.min(PROFILE_CROP_ZOOM_MIN, cropFitZoom(crop));

// Draw at the same position as the preview; the canvas clips overflow and keeps
// empty margins transparent when the image is reduced below the frame size.
export const cropExportGeometry = (crop) => {
  const frame = cropFrameDimensions(crop.target, crop);
  const output = cropOutputDimensions(crop.target);
  const display = cropDisplaySize(crop);
  if (!display.width || !display.height) return null;
  const scale = Math.min(
    output.width / frame.width,
    output.height / frame.height,
    crop.naturalWidth / display.width,
    crop.naturalHeight / display.height
  );
  const width = Math.max(1, Math.round(frame.width * scale));
  const height = Math.max(1, Math.round(frame.height * scale));
  const renderScale = Math.min(scale, width / frame.width, height / frame.height);
  return {
    width,
    height,
    imageX: (width - display.width * renderScale) / 2 + crop.x * renderScale,
    imageY: (height - display.height * renderScale) / 2 + crop.y * renderScale,
    imageWidth: display.width * renderScale,
    imageHeight: display.height * renderScale,
  };
};

export const reorderItems = (items, fromIndex, toIndex) => {
  const next = Array.from(items);
  const [removed] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, removed);
  return next;
};

export const normalizeThemeConfig = (theme = {}) => {
  const merged = {
    ...DEFAULT_THEME,
    ...(theme || {}),
  };
  const customColors = buildCustomColorsFromTheme(merged);

  return {
    ...merged,
    accent: customColors.enabled ? customColors.accent : merged.accent,
    customColors,
  };
};

export const normalizeConfig = (config = {}) => ({
  hoursCopyOptions: Array.isArray(config.hoursCopyOptions) ? config.hoursCopyOptions : [],
  active: config.active !== false,
  publicUrl: config.public_url || '',
  publicPath: config.public_path || '',
  restaurant: {
    ...FALLBACK_RESTAURANT,
    ...(config.restaurant || {}),
    showHours: hasRegisteredOpeningHours(config.restaurant?.openingHours) && config.restaurant?.showHours !== false,
  },
  products: Array.isArray(config.products) && config.products.length
    ? config.products
    : DEFAULT_PRODUCT_STATE,
  externalLinks: Array.isArray(config.externalLinks)
    ? config.externalLinks
    : [],
  buttonOrder: Array.isArray(config.buttonOrder)
    ? config.buttonOrder
    : [],
  theme: normalizeThemeConfig(config.theme),
});

export const configPayload = (config) => ({
  active: config.active,
  restaurant: config.restaurant,
  products: config.products,
  externalLinks: config.externalLinks,
  buttonOrder: config.buttonOrder,
  theme: config.theme,
});
